import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {Asset, ASSET_STATUS_MISSING_INFO, AssetInfo, AssetSaleInfo, AssetStatus} from '../shared/domain/item';
import {Router} from '@angular/router';
import {ItemSummaryOpportunityService} from '../shared/opportunity/item-summary-opportunity.service';

import {
  ASSET_PACKAGE_STATUS_CONTRACTED,
  ASSET_PACKAGE_STATUS_IN_PRICING,
  ASSET_PACKAGE_STATUS_RESENT_FOR_PRICING,
  ASSET_PACKAGE_STATUS_SENT_FOR_PRICING,
  AssetPackageDto
} from '../shared/domain/asset-package';
import {ItemSummaryAssetControlGroupService} from '../shared/asset-package/item-summary-asset-control-group.service';

import {Opportunity, OpportunityPackageVisibility} from '../shared/domain/opportunity';
import {ItemSummaryItemSummaryService} from '../shared/asset/item-summary-item-summary.service';
import {permissionType, PermissionUtils} from '../shared/permission';
import {UserSessionService} from '@rbaip/ui-component';

import {ItemSummaryEventService} from '../shared/auction-event/item-summary-event.service';
import {ItemSummaryEventServiceImpl} from '../shared/auction-event/item-summary-event-impl.service';
import {AuctionEvent} from '../shared/domain/auction-event';
import {ItemSummaryItemService} from '../shared/asset/item-summary-item.service';
import {ItemViewType} from '../shared/item-view-type.enum';
import {appConfig} from '../shared/app.config';
import {ITEM_SUMMARY_EXTERNAL_CONFIG_TOKEN} from '../shared/domain';

@Component({
  selector: 'lib-item-summary-item-summary-common',
  templateUrl: './item-summary-common.component.html',
  styleUrls: ['./item-summary-common.component.scss'],
  providers: [{provide: ItemSummaryEventService, useClass: ItemSummaryEventServiceImpl}]
})
export class ItemSummaryCommonComponent implements OnInit, OnDestroy {

  @Input() itemId: number;
  @Input() fromView: ItemViewType;
  @Input() editAssetFromPricing: boolean;
  @Input() itemSummaryService: ItemSummaryItemSummaryService;
  @Input() itemService: ItemSummaryItemService;

  @Output() assetLoaded = new EventEmitter<Asset>();
  @Output() assetInfoLoaded = new EventEmitter<AssetInfo>();
  @Output() assetSaleInfoLoaded = new EventEmitter<AssetSaleInfo>();
  @Output() assetPackageLoaded = new EventEmitter<AssetPackageDto>();
  @Output() opportunityLoaded = new EventEmitter<Opportunity>();
  @Output() auctionEventLoaded = new EventEmitter<AuctionEvent>();
  @Output() editModeChanged = new EventEmitter<boolean>();

  asset: Asset;
  assetInfo: AssetInfo;
  public assetControlGroup: AssetPackageDto;
  private assetInfoServiceSubscription: Subscription;
  private opportunitySubscription: Subscription;
  private auctionEventSubscription: Subscription;
  private currentUserChangedSubscription: Subscription;
  assetSaleInfo: AssetSaleInfo;
  currentDefaultSaleInfo: AssetSaleInfo;
  opportunity: Opportunity;
  private isAuctionEventActive = false;
  public isVSOEvent = false;
  canMoveAsset: boolean;
  doneLoading = false;
  editModeFlags: boolean[] = [];
  dateFormat: string = appConfig.monthDateFormat;
  public assetMissingInfo = ASSET_STATUS_MISSING_INFO;

  constructor(private opportunityService: ItemSummaryOpportunityService,
              private router: Router,
              private eventService: ItemSummaryEventService,
              private assetControlGroupService: ItemSummaryAssetControlGroupService,
              private userSessionService: UserSessionService,
              @Optional() @Inject(ITEM_SUMMARY_EXTERNAL_CONFIG_TOKEN) public appSettings: any) {
    this.subscribeUserChanged();
  }

  ngOnInit() {
    this.subscribeSubscription();
    this.init();
  }


  init() {
    this.loadPagePermission();
    setTimeout(() => {
      this.itemSummaryService.getItem(this.itemId);
    });
  }

  private subscribeUserChanged() {
    this.currentUserChangedSubscription = this.userSessionService.userChanged
      .subscribe((siteUser) => {
        if (siteUser) {
          this.init();
        }
      });
  }


  private subscribeSubscription(): void {
    this.subscribeCurrentUserChanged();
    this.subscribeItemInfo();
    this.subscribeAuctionEvent();
  }

  private subscribeItemInfo(): void {
    this.assetInfoServiceSubscription = this.itemSummaryService.assetServiceCallback.subscribe(item => {
      this.asset = item;
      this.assetLoaded.emit(this.asset);
      this.loadAssetInformation();
      this.retrieveAssetPackageInfoIfAny();
      if (this.asset && this.asset.auctionEventId) {
        this.eventService.getAuctionEventDetails(this.asset.auctionEventId);
      }
      this.doneLoading = true;
    });
  }

  loadAssetInformation() {
    this.assetInfo = AssetInfo.getAssetInfoFromAsset(this.asset);
    if (this.assetInfo) {
      this.assetInfo.canSaveAssetInfo = true;
      this.assetInfo.displayInAssetSummary = true;
    }
    this.assetInfoLoaded.emit(this.assetInfo);
  }

  private retrieveAssetPackageInfoIfAny(): void {
    if (this.asset.assetControlGroupId !== null && this.asset.assetControlGroupId !== undefined && this.asset.assetControlGroupId > 0) {
      this.assetControlGroupService.getAssetPackageInfo(this.asset.assetControlGroupId).subscribe(assetPackageJson => {
        this.assetControlGroup = AssetPackageDto.constructFromObjectJson(assetPackageJson);
        this.assetPackageLoaded.emit(this.assetControlGroup);
        this.loadSaleInfo();
        if (this.assetControlGroup && this.assetControlGroup.opportunityId) {
          this.loadOpportunityDetails();
        }
      });
    } else {
      this.loadSaleInfo();
    }
  }

  onAssetChanged(asset: Asset): void {
    this.asset = asset ? asset : this.asset;
    this.assetLoaded.emit(this.asset);
    this.loadSaleInfo();
  }

  private loadSaleInfo(): void {
    this.currentDefaultSaleInfo = AssetSaleInfo.getSaleInfoFromAsset(this.asset, this.assetControlGroup);
    this.assetSaleInfo = AssetSaleInfo.getSaleInfoFromAsset(this.asset, this.assetControlGroup);
    this.assetSaleInfo.displayPrices = true;
    this.assetSaleInfo.displayListingFee = true;
    this.assetSaleInfo.displaySetDefaultCheckBox = false;
    this.assetSaleInfo.isEditable = false;
    if (this.assetSaleInfo.saleType) {
      this.asset.setDefault = false;
    }
    this.assetSaleInfoLoaded.emit(this.assetSaleInfo);
  }

  private loadOpportunityDetails() {
    if (this.assetControlGroup && this.assetControlGroup.opportunityId) {
      this.opportunitySubscription = this.opportunityService.getOpportunityDetails(this.assetControlGroup.opportunityId).subscribe(
        opportunity => {
          this.opportunity = opportunity;
          this.opportunityLoaded.emit(this.opportunity);
          this.opportunityService.selectedOpportunityChanged.next(opportunity);
        });
    }
  }

  ngOnDestroy() {
    if (this.assetInfoServiceSubscription) {
      this.assetInfoServiceSubscription.unsubscribe();
    }
    if (this.opportunitySubscription) {
      this.opportunitySubscription.unsubscribe();
    }
    if (this.auctionEventSubscription) {
      this.auctionEventSubscription.unsubscribe();
    }
    if (this.currentUserChangedSubscription) {
      this.currentUserChangedSubscription.unsubscribe();
    }
  }

  shouldShowOnlyOpportunityInfo(): boolean {
    return !this.isAssociatedPackage()
      && this.assetControlGroup && !this.assetControlGroup.isPackageConfidential();
  }

  isEditAssetSaleInfoEnabled(): boolean {
    return this.assetSaleInfo && !this.assetSaleInfo.isEditable && (!this.isPackageIsInPricing() || this.editAssetFromPricing) && !!this.assetControlGroup && !this.assetControlGroup.isSalesHubNonMarsContract;
  }

  shouldShowLienInfo(): boolean {
    return this.isAssociatedPackage();
  }

  isAssociatedPackage(): boolean {
    return this.assetControlGroup &&
      this.assetControlGroup.opportunityId > 0
      && this.assetControlGroup.opportunityRecordType !== OpportunityPackageVisibility.TYPE_CONFIDENTIAL;
  }

  public isPackageIsInPricing(): boolean {
    return this.assetControlGroup && !this.asset.equipId
      && (this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_SENT_FOR_PRICING
        || this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_IN_PRICING
        || this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_RESENT_FOR_PRICING
        || this.isPackageContractedAndLocked()
      );
  }
  public isPackageContractedAndLocked(): boolean {
    if(this.editAssetFromPricing) return false;

    return this.assetControlGroup && this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_CONTRACTED &&
      this.assetControlGroup.lockYn === 'Y';
  }

  updateAssetInformation(assetInfo) {
    if ( assetInfo ) {
      this.assetInfo = assetInfo;
      this.assetInfo.canSaveAssetInfo = true;
      this.init();
    }
  }

  private subscribeAuctionEvent(): void {
    this.auctionEventSubscription = this.eventService.auctionEventCallback.subscribe(
      (response) => {
        const auctionEvent = response;
        this.auctionEventLoaded.emit(auctionEvent);
        if (auctionEvent) {
          this.isAuctionEventActive = auctionEvent.activeAuction;
          this.isVSOEvent = auctionEvent.allowsIpInspection;
        }
      });
  }

  private loadPagePermission() {
    this.canMoveAsset = PermissionUtils.hasPermission(this.userSessionService, permissionType.canMoveAsset);
  }

  private subscribeCurrentUserChanged() {
    this.currentUserChangedSubscription = this.userSessionService.userChanged.subscribe((siteUser) => {
      if (siteUser) {
        this.init();
      }
    });
  }

  isEditAssetInfoEnabled(): boolean {
    return this.assetInfo && !this.assetInfo.editAssetInformation &&
      ( this.isEquipmentInfoEditable() || !this.asset.equipId) &&
      (!this.isPackageIsInPricing() || this.editAssetFromPricing) && !!this.assetControlGroup &&
      !this.assetControlGroup.isSalesHubNonMarsContract;
  }

  private isEquipmentInfoEditable(): boolean {
    if (this.appSettings && !this.appSettings.enableAssetManagement) {
      return false;
    }
    return this.assetInfo && AssetStatus.isBelongingToPreSaleTab(this.asset.assetStatusId);
  }

  isEditAssetServicesEnabled() {
    return !this.isPackageIsInPricing() && !!this.assetControlGroup && !this.assetControlGroup.isSalesHubNonMarsContract;
  }

  onEditModeChanged(isOnEditMode: boolean, componentName: string) {
    if (componentName === 'Asset Info') {
      this.editModeFlags[0] = isOnEditMode;
    } else if (componentName === 'Sale Info') {
      this.editModeFlags[1] = isOnEditMode;
    } else if (componentName === 'Budget Info') {
      this.editModeFlags[2] = isOnEditMode;
    }

    let isOneOfComponentsOnEditMode = false;
    for (const editModeFlag of this.editModeFlags) {
      isOneOfComponentsOnEditMode = isOneOfComponentsOnEditMode || editModeFlag;
    }
    this.editModeChanged.emit(isOneOfComponentsOnEditMode);
  }
}
